import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import jsPDF from "jspdf"
import moment from "moment"
import React, { useEffect, useMemo, useState } from "react"
import { FaSearch, FaEye, FaFilePdf, FaChevronCircleLeft, FaChevronCircleRight } from "react-icons/fa"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import { fetchData } from "utils/Common_api_functions"

const FeedbackTable = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [searchString, setSearchString] = useState("")
  const [perPageItem, setPerPageItem] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [selectedFeedback, setSelectedFeedback] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    fetchFeedback()
  }, [])

  async function fetchFeedback() {
    const APIdata = await fetchData(setLoading, "feedback")
    if (APIdata?.success) {
      setData(APIdata?.data)
    }
  }

  /* ================= FILTER ================= */
  const filteredData = useMemo(() => {
    if (!searchString) return data

    const query = searchString.toLowerCase()

    return data.filter(item => {
      const searchableText = [
        item?.id,
        item?.employee?.name,
        item?.location?.name,
        item?.location?.address,
        item?.checkedAndAuthorisedBy,
        item?.position,
        item?.isWorkSatisfied ? "yes" : "no",
      ]
        .filter(Boolean) // remove null/undefined
        .join(" ")
        .toLowerCase()

      return searchableText.includes(query)
    })
  }, [data, searchString])

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredData.length / perPageItem)
  const start = (currentPage - 1) * perPageItem
  const paginatedData = filteredData.slice(start, start + perPageItem)

  /* ================= MODAL ================= */
  const openModal = item => {
    setSelectedFeedback(item)
    setModalOpen(true)
  }

  // Replace the handleDownload function:
  const handleDownload = item => {
    const doc = new jsPDF()
    const feedback = item

    const primaryColor = [41, 128, 185]
    const lightGray = [245, 245, 245]
    const darkText = [30, 30, 30]

    let y = 20

    // ── Header ──────────────────────────────────────────
    doc.setFillColor(...primaryColor)
    doc.rect(0, 0, 210, 22, "F")

    doc.setTextColor(255, 255, 255)
    doc.setFontSize(16)
    doc.setFont("helvetica", "bold")
    doc.text("Window Cleaner Feedback Report", 14, 14)
    y = 32

    // ── Employee & Location ──────────────────────────────
    doc.setFillColor(...lightGray)
    doc.rect(10, y, 190, 28, "F")

    doc.setTextColor(...darkText)
    doc.setFontSize(13)
    doc.setFont("helvetica", "bold")
    doc.text(feedback?.employee?.name || "—", 14, y + 9)

    doc.setFontSize(10)
    doc.setFont("helvetica", "normal")
    doc.setTextColor(100, 100, 100)
    doc.text(feedback?.location?.name || "—", 14, y + 17)
    doc.text(feedback?.location?.address || "—", 14, y + 23)

    // Work Satisfied badge (right side)
    const satisfied = feedback?.isWorkSatisfied
    doc.setFillColor(...(satisfied ? [39, 174, 96] : [231, 76, 60]))
    doc.roundedRect(148, y + 6, 35, 12, 3, 3, "F")
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(10)
    doc.setFont("helvetica", "bold")
    doc.text(satisfied ? "Work Satisfied" : "Not Satisfied", 165, y + 13, {
      align: "center",
    })

    y += 38

    // ── Cleaning Checklist ───────────────────────────────
    doc.setTextColor(...primaryColor)
    doc.setFontSize(12)
    doc.setFont("helvetica", "bold")
    doc.text("Cleaning Checklist", 14, y)
    y += 6

    doc.setDrawColor(200, 200, 200)
    doc.line(14, y, 196, y)
    y += 6

    const checklistItems = [
      { label: "Window Cleaning", value: feedback?.isWindowCleaning },
      { label: "Outside Windows", value: feedback?.hasCleanedOutsideWindow },
      { label: "Inside Windows", value: feedback?.hasCleanedInsideWindow },
      { label: "Big Clean", value: feedback?.isBigClean },
    ]

    checklistItems.forEach((chk, i) => {
      const col = i % 2
      const row = Math.floor(i / 2)
      const x = col === 0 ? 14 : 110
      const cy = y + row * 16

      doc.setFillColor(...lightGray)
      doc.rect(x, cy, 90, 12, "F")

      doc.setTextColor(...darkText)
      doc.setFontSize(10)
      doc.setFont("helvetica", "normal")
      doc.text(chk.label, x + 4, cy + 8)

      doc.setFillColor(...(chk.value ? [39, 174, 96] : [231, 76, 60]))
      doc.roundedRect(x + 63, cy + 2, 22, 8, 2, 2, "F")
      doc.setTextColor(255, 255, 255)
      doc.setFont("helvetica", "bold")
      doc.text(chk.value ? "Yes" : "No", x + 74, cy + 7.5, { align: "center" })
    })

    y += Math.ceil(checklistItems.length / 2) * 16 + 8

    // ── Authorization ────────────────────────────────────
    doc.setTextColor(...primaryColor)
    doc.setFontSize(12)
    doc.setFont("helvetica", "bold")
    doc.text("Authorization", 14, y)
    y += 6

    doc.setDrawColor(200, 200, 200)
    doc.line(14, y, 196, y)
    y += 8

    doc.setTextColor(130, 130, 130)
    doc.setFontSize(9)
    doc.setFont("helvetica", "normal")
    doc.text("Checked & Authorised By", 14, y)
    doc.text("Position", 110, y)
    y += 5

    doc.setTextColor(...darkText)
    doc.setFontSize(11)
    doc.setFont("helvetica", "bold")
    doc.text(feedback?.checkedAndAuthorisedBy || "—", 14, y)
    doc.text(feedback?.position || "—", 110, y)
    y += 10

    // ── Signature ────────────────────────────────────────
    doc.setTextColor(130, 130, 130)
    doc.setFontSize(9)
    doc.setFont("helvetica", "normal")
    doc.text("Signature", 14, y)
    y += 5

    if (feedback?.signature?.startsWith("data:image")) {
      try {
        doc.addImage(feedback.signature, "PNG", 14, y, 60, 30)
        y += 34
      } catch {
        doc.setTextColor(...darkText)
        doc.setFontSize(10)
        doc.text("[Signature Image]", 14, y + 6)
        y += 12
      }
    } else {
      doc.setTextColor(...darkText)
      doc.setFontSize(10)
      doc.setFont("helvetica", "normal")
      doc.text(feedback?.signature || "—", 14, y + 6)
      y += 12
    }

    // ── Footer ───────────────────────────────────────────
    doc.setDrawColor(200, 200, 200)
    doc.line(14, y + 4, 196, y + 4)
    y += 10

    doc.setTextColor(130, 130, 130)
    doc.setFontSize(9)
    doc.text(
      `Submitted on: ${moment(feedback?.createdAt).format("DD/MM/YYYY | hh:mm A")}`,
      14,
      y
    )

    doc.save(`feedback-${feedback?.id || "report"}.pdf`)
  }

  return (
    <div className="page-content">
      <Container fluid>
        {loading && <Loader />}

        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Window Cleaner Feedback"
          description="View all Window Cleaner Client's Feedback"
        />

        <Row>
          <Col lg="12">
            <Card className="shadow-lg">
              <CardBody>
                {/* SEARCH + PAGE SIZE */}
                <div className="d-flex justify-content-start mb-3 flex-wrap gap-3">
                  <div className="position-relative">
                    <input
                      className="form-control rounded-4 ps-3 pe-5"
                      placeholder="Search feedback..."
                      value={searchString}
                      onChange={e => {
                        setSearchString(e.target.value)
                        setCurrentPage(1)
                      }}
                    />
                    <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                  </div>

                  <select
                    className="form-select rounded-4 w-auto"
                    value={perPageItem}
                    onChange={e => {
                      setPerPageItem(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                {/* TABLE */}
                <Table bordered hover responsive>
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Employee</th>
                      <th>Location</th>
                      <th>Authority</th>
                      <th>Position</th>
                      <th>Work Satisfaction</th>
                      <th>Submission Date</th>
                      <th>View</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No feedback found
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map(item => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td>{item?.employee?.name ?? 'No Employee Linked'}</td>
                          <td>{item?.location?.name ?? 'No Location Linked'}</td>
                          <td>{item?.checkedAndAuthorisedBy}</td>
                          <td>{item?.position}</td>
                          <td>{item?.isWorkSatisfied ? "Yes" : "No"}</td>
                          <td>
                            {moment(item?.createdAt).format(
                              "DD/MM/YYYY hh:mm a"
                            )}
                          </td>
                          <td>
                            <Button
                              color="info"
                              onClick={() => openModal(item)}
                            >
                              <FaEye />
                            </Button>
                            <Button
                              className="ms-2"
                              color="warning"
                              onClick={() => handleDownload(item)}
                            >
                              <FaFilePdf />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                {/* PAGINATION */}
                <div className="d-flex justify-content-between gap-2 mt-3">
                  <p className="mb-0">
                    Showing {paginatedData.length} out of {filteredData.length}{" "}
                    Feedbacks.
                  </p>
                  <div>
                    <Button
                      size="sm"
                      disabled={currentPage === 1}
                      className="fs-6"
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      <FaChevronCircleLeft />
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Button
                        key={i}
                        size="sm"
                        className="mx-2 fs-6"
                        color={currentPage === i + 1 ? "primary" : "light"}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      className="fs-6"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      <FaChevronCircleRight />
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ================= MODAL ================= */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setModalOpen(false)}>
          Feedback Details
        </ModalHeader>

        <ModalBody>
          {selectedFeedback && (
            <div className="feedback-modal">
              {/* ================= HEADER CARD ================= */}
              <div className="card shadow-sm mb-4 border-0">
                <div className="card-body d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1 fw-bold">
                      {selectedFeedback.employee?.name ?? 'No Employee Linked'}
                    </h5>
                    <p className="mb-0 text-muted">
                      {selectedFeedback.location?.name ?? 'No Location Linked'}
                    </p>
                    <small className="text-muted">
                      {selectedFeedback.location?.address ?? '—'}
                    </small>
                  </div>

                  <span
                    className={`badge px-3 py-2 ${
                      selectedFeedback.isWorkSatisfied
                        ? "bg-success"
                        : "bg-danger"
                    }`}
                  >
                    {selectedFeedback.isWorkSatisfied
                      ? "Work Satisfied"
                      : "Not Satisfied"}
                  </span>
                </div>
              </div>

              {/* ================= CLEANING DETAILS ================= */}
              <div className="card shadow-sm mb-4 border-0">
                <div className="card-body">
                  <h6 className="fw-bold mb-3 text-primary">
                    Cleaning Checklist
                  </h6>

                  <div className="row g-3">
                    {[
                      {
                        label: "Window Cleaning",
                        value: selectedFeedback.isWindowCleaning,
                      },
                      {
                        label: "Outside Windows",
                        value: selectedFeedback.hasCleanedOutsideWindow,
                      },
                      {
                        label: "Inside Windows",
                        value: selectedFeedback.hasCleanedInsideWindow,
                      },
                      {
                        label: "Big Clean",
                        value: selectedFeedback.isBigClean,
                      },
                    ].map((item, index) => (
                      <div className="col-md-6" key={index}>
                        <div className="d-flex justify-content-between align-items-center p-3 rounded bg-light">
                          <span className="fw-medium">{item.label}</span>
                          <span
                            className={`badge ${
                              item.value ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {item.value ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ================= AUTHORIZATION ================= */}
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="fw-bold mb-3 text-primary">Authorization</h6>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <small className="text-muted">
                        Checked & Authorised By
                      </small>
                      <p className="fw-semibold mb-0">
                        {selectedFeedback.checkedAndAuthorisedBy}
                      </p>
                    </div>

                    <div className="col-md-6 mb-3">
                      <small className="text-muted">Position</small>
                      <p className="fw-semibold mb-0">
                        {selectedFeedback.position}
                      </p>
                    </div>
                  </div>

                  <div className="d-block">
                    <small className="text-muted d-block">Signature</small>

                    {selectedFeedback?.signature?.startsWith("data:image") ? (
                      <img
                        src={selectedFeedback.signature}
                        alt="Signature"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "100px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          marginTop: "6px",
                        }}
                      />
                    ) : (
                      <p className="mb-0 text-muted">
                        {selectedFeedback?.signature || "—"}
                      </p>
                    )}
                  </div>

                  <hr />

                  <small className="text-muted">
                    Submitted on{" "}
                    {moment(selectedFeedback.createdAt).format("DD/MM/YYYY | hh:mm A")}
                  </small>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default FeedbackTable
